import React from 'react';

import styled from 'styled-components';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as ThreeDActions from 'modules/threed';
import { Progress, Spin, Card, Button, Divider, message, Select } from 'antd';

import CogniteViewer from '@cognite/3d-viewer';
import { AssetInfo } from 'components/AssetInfo';

const { Option } = Select;

const StyledCard = styled(Card)`
  position: absolute !important;
  bottom: 40px;
  left: 40px;
  max-width: 50%;
  width: 300px;
  display: ${props => (props.visible === 'true' ? 'block' : 'none')};
`;

const ControlSection = styled(Card)`
  position: absolute !important;
  bottom: 40px;
  right: 40px;
  max-width: 50%;
  width: 300px;
`;

const AssetDetailsSection = styled(Card)`
  position: absolute !important;
  bottom: 160px;
  top: 100px;
  right: 40px;
  max-width: 50%;
  width: 500px;
  overflow-y: scroll;
  button {
    margin-bottom: 12px;
  }
`;

const Wrapper = styled.div`
  position: absolute;
  height: 100%;
  width: calc(100% + 32px);
  margin: -16px;
  overflow: hidden;

  canvas {
    height: 100%;
    width: 100%;
  }
`;

const StyledHeading = styled.h3`
  position: absolute !important;
  background: #efefef;
  padding: 20px 30px;
  top: 40px;
  left: 40px;
  max-width: 50%;
  display: ${props => (props.visible === 'true' ? 'block' : 'none')};
`;

class HomeContainer extends React.Component {
  state = {
    showLoader: false,
    showWarning: true,
    mounted: true,
    nodeAssetDetails: {
      asset: undefined,
    },
    mode: 'asset',
    control: 'orbit',
  };
  async componentDidMount() {
    if (this.props.threed.viewer === null) {
      this.props.setupViewer();
    } else if (!this.props.threed.rendered) {
      this.renderThreeDViewer();
    }
    setTimeout(() => {
      this.setState({ showWarning: false });
    }, 5000);
  }
  async componentDidUpdate() {
    const { viewer, rendered } = this.props.threed;
    if (viewer !== null && !rendered) {
      this.renderThreeDViewer();
    }
  }
  componentWillUnmount() {
    if (this.props.threed.rendered) {
      this.props.threed.viewer.stop();
      this.props.unmountViewer();
    }
  }

  onNodeClickListener = async nodeId => {
    const { viewer } = this.props.threed;
    if (this.state.mode === 'asset') {
      try {
        this.setState({ nodeAssetDetails: { asset: null } });
        const { data: nodeAssetDetails } = await viewer.getAssetFromNodeId(
          nodeId
        );
        this.highlightNodes(nodeAssetDetails.childIds);
        this.setState({ nodeAssetDetails });
      } catch (e) {
        this.highlightNodes([]);
        this.setState({ nodeAssetDetails: { asset: undefined } });
        message.error('Could not find associated asset');
      }
    } else {
      viewer.toggleNodeSelected(nodeId);
    }
  };

  clearAssetDetails = () => {
    const { viewer } = this.props.threed;
    viewer.deselectAllNodes();
    this.setState({ nodeAssetDetails: { asset: undefined } });
  };

  highlightNodes = (nodeIds = []) => {
    const { viewer } = this.props.threed;
    viewer.deselectAllNodes();
    nodeIds.forEach(id => viewer.toggleNodeSelected(id));
  };

  changeMode = value => {
    this.clearAssetDetails();
    this.setState({ mode: value });
  };

  changeControl = value => {
    const { viewer } = this.props.threed;
    this.clearAssetDetails();
    this.setState({ control: value });
    viewer.setControlType(value);
  };

  renderThreeDViewer = async () => {
    const { viewer } = this.props.threed;
    viewer.setOnClickListener(this.onNodeClickListener);
    viewer.setOnDoubleClickListener(() => {
      // notification.open({
      //   message: 'Asset Double Clicked',
      //   description: `Node ID: ${nodeId}`,
      // });
    });
    viewer.setProgressListener((step, totalSteps, subprogress, text) => {
      this.setState({
        step,
        totalSteps,
        progress:
          subprogress && Number.isFinite(subprogress)
            ? Math.round(subprogress * 100)
            : 100,
        loadingText: text,
        showLoader: true,
      });
      if (step === totalSteps) {
        setTimeout(() => {
          this.setState({
            showLoader: false,
          });
        }, 2000);
      }
    });
    this.container.appendChild(viewer.canvas);
    viewer.start();
    this.props.renderViewer();
  };
  render() {
    return (
      <Wrapper
        innerRef={container => {
          this.container = container;
        }}
      >
        <StyledHeading visible={this.state.showWarning.toString()}>
          Currently you can only preview Ivar Aasen
        </StyledHeading>
        <StyledCard visible={this.state.showLoader.toString()}>
          <Spin
            tip={`Step ${this.state.step} of ${this.state.totalSteps}, ${
              this.state.loadingText
            }`}
          />
          <Progress
            percent={this.state.progress}
            {...(this.state.step !== this.state.totalSteps
              ? { status: 'active' }
              : null)}
            showInfo={false}
          />
        </StyledCard>
        <ControlSection>
          <Select
            defaultValue={this.state.mode}
            onChange={this.changeMode}
            placeholder="Mode"
          >
            <Option value="asset">Asset Picker</Option>
            <Option value="design">Design</Option>
          </Select>
          <Divider type="vertical" />
          <Select
            defaultValue={this.state.control}
            onChange={this.changeControl}
            placeholder="Control Scheme"
          >
            <Option value="fpv">First Person</Option>
            <Option value="orbit">Orbit</Option>
          </Select>
        </ControlSection>
        {this.state.nodeAssetDetails.asset !== undefined ? (
          <AssetDetailsSection>
            <Button type="primary" onClick={this.clearAssetDetails}>
              Clear Asset Info
            </Button>
            <AssetInfo asset={this.state.nodeAssetDetails.asset} />
          </AssetDetailsSection>
        ) : null}
      </Wrapper>
    );
  }
}

HomeContainer.propTypes = {
  threed: PropTypes.shape({
    rendered: PropTypes.bool.isRequired,
    viewer: PropTypes.instanceOf(CogniteViewer),
  }).isRequired,
  setupViewer: PropTypes.func.isRequired,
  renderViewer: PropTypes.func.isRequired,
  unmountViewer: PropTypes.func.isRequired,
};

function mapDispatchToProps(dispatch) {
  return bindActionCreators(ThreeDActions, dispatch);
}
export default connect(
  state => ({
    threed: state.threed,
  }),
  mapDispatchToProps
)(HomeContainer);



// WEBPACK FOOTER //
// ./src/containers/ThreeDViewerContainer.js